import React from 'react'

import { usePortal } from '../../../hooks'
import { Dialog, DialogProps } from '../Dialog'

export type DialogOption = Omit<DialogProps, 'open' | 'onCloseEnd'>

interface DialogContextValue {
  openDialog: (dialog: DialogOption) => void
}

const uninitialized = () => {
  throw new Error('DialogProvider not initialized')
}

const DialogContext = React.createContext<DialogContextValue>({
  openDialog: uninitialized,
})

export interface DialogProviderProps {
  disablePortal?: boolean
  children: React.ReactNode
}

export const DialogProvider: React.FC<DialogProviderProps> = ({
  disablePortal,
  children,
}) => {
  const [dialogOptions, setDialogOptions] = React.useState<DialogProps | null>(
    null,
  )

  const { Portal } = usePortal()

  const openDialog = React.useCallback((dialogOptions: DialogOption) => {
    setDialogOptions({ ...dialogOptions, open: true })
  }, [])

  const handleClose = React.useCallback(() => {
    dialogOptions?.onClose?.()
    setDialogOptions((p) => (p ? { ...p, open: false } : null))
  }, [dialogOptions])

  const handleClosed = React.useCallback(() => {
    setDialogOptions(null)
  }, [])

  const value = React.useMemo(() => ({ openDialog }), [openDialog])

  const dialog = dialogOptions ? (
    <Dialog
      {...dialogOptions}
      onClose={handleClose}
      onCloseEnd={handleClosed}
    />
  ) : null

  return (
    <DialogContext.Provider value={value}>
      {children}
      {!disablePortal ? <Portal>{dialog}</Portal> : dialog}
    </DialogContext.Provider>
  )
}

export const useDialog = () => {
  return React.useContext(DialogContext)
}
